import { memo, useId } from 'react';
import { DESIGN_HEIGHT, DESIGN_WIDTH, toPoints } from './geometry';
import { regionOutline } from './scenes/regions';
import { SCENES } from './scenes';
import type { SceneDefinition, SceneId } from './scenes';
import { useRovingRadio } from './useRovingRadio';

const VIEW_BOX = `0 0 ${String(DESIGN_WIDTH)} ${String(DESIGN_HEIGHT)}`;

interface SceneThumbProps {
  scene: SceneDefinition;
  /** Flat colour laid on the stone surfaces (the chosen stone's average colour). */
  tint: string;
}

/**
 * A miniature of a scene: the drawn room with its stone surfaces filled with one flat colour. Cheap —
 * no textures, no planes — so all four stay on screen.
 */
const SceneThumb = memo(function SceneThumb({ scene, tint }: SceneThumbProps) {
  const uid = useId().replace(/[^a-zA-Z0-9]/g, '');
  const ids = `${uid}${scene.id}`;
  return (
    <svg
      className="viz-thumb"
      viewBox={VIEW_BOX}
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
      focusable="false"
    >
      <scene.Back ids={ids} />
      {scene.regions.map((region) => (
        <polygon key={region.id} points={toPoints(regionOutline(region))} fill={tint} />
      ))}
      <g className="viz-thumb__shade">
        <scene.Shade ids={ids} />
      </g>
      <scene.Front ids={ids} />
    </svg>
  );
});

export interface SceneSwitcherProps {
  value: SceneId;
  onChange: (scene: SceneId) => void;
  /** Id of the heading that names the group. */
  labelledBy: string;
  tint: string;
  names: Readonly<Record<SceneId, string>>;
}

/**
 * The scene picker: a radio group of four thumbnails (WAI-ARIA radio-group pattern — one tab stop,
 * arrows move and select, wrapping).
 */
export const SceneSwitcher = memo(function SceneSwitcher({
  value,
  onChange,
  labelledBy,
  tint,
  names,
}: SceneSwitcherProps) {
  const selected = SCENES.findIndex((scene) => scene.id === value);
  const item = useRovingRadio<HTMLButtonElement>({
    count: SCENES.length,
    selected,
    onSelect: (index) => {
      const scene = SCENES[index];
      if (scene) onChange(scene.id);
    },
  });

  return (
    <div className="viz-scenes" role="radiogroup" aria-labelledby={labelledBy}>
      {SCENES.map((scene, index) => (
        <button key={scene.id} type="button" className="viz-scene" {...item(index)}>
          <SceneThumb scene={scene} tint={tint} />
          <span className="viz-scene__name">{names[scene.id]}</span>
        </button>
      ))}
    </div>
  );
});
